/**
 * FI (Finanzas) module — shared frontend type contract.
 *
 * These interfaces are the CONTRACT between the worker's FI endpoints,
 * the api.ts client methods, and every FI component. Backend responses
 * and api.ts declarations must match these shapes exactly.
 */

// ============ Deals / pipeline ============

export type DealStage = 'prospect' | 'poc' | 'proposal' | 'won' | 'lost'
export type DealType = 'managed' | 'professional' | 'hybrid'

export const DEAL_STAGES: DealStage[] = ['prospect', 'poc', 'proposal', 'won', 'lost']
export const OPEN_STAGES: DealStage[] = ['prospect', 'poc', 'proposal']
export const STAGE_DEFAULT_PROBABILITY: Record<DealStage, number> = {
  prospect: 10, poc: 30, proposal: 60, won: 100, lost: 0,
}
export const STAGE_LABEL: Record<DealStage, string> = {
  prospect: 'Prospecto', poc: 'POC', proposal: 'Propuesta', won: 'Ganado', lost: 'Perdido',
}

export interface Deal {
  id: string
  name: string
  slug?: string | null
  stage: DealStage
  dealType: DealType
  projectedMrr: number
  projectedOneTime: number
  probability: number
  currency: string
  expectedCloseDate?: string | null
  estimatedUsers?: number | null
  plan?: string | null
  packageId?: string | null
  ownerUserId?: string | null
  contactName?: string | null
  contactEmail?: string | null
  contactPhone?: string | null
  source?: string | null
  isIntragroup: boolean
  notes?: string | null
  lostReason?: string | null
  stageChangedAt?: string | null
  wonAt?: string | null
  lostAt?: string | null
  convertedTenantId?: string | null
  convertedClientId?: string | null
  createdAt: string
  updatedAt: string
}

export interface DealStageSummary {
  stage: DealStage
  count: number
  rawMrr: number
  rawOneTime: number
  weightedMrr: number       // Σ projectedMrr × probability/100
  weightedOneTime: number
}

export interface DealsSummary {
  byStage: DealStageSummary[]
  openCount: number
  weightedPipelineMrr: number      // open stages only
  weightedPipelineOneTime: number
  wonThisMonth: number             // count
  lostThisMonth: number
}

export interface DealForecastPoint {
  year: number
  month: number
  label: string
  weightedMrr: number
  weightedOneTime: number
}

// ============ Costs ============

export type CostType = 'per_user_monthly' | 'fixed_monthly' | 'one_time_per_unit'

export interface VendorCostRate {
  id: string
  vendor: string
  serviceId?: string | null
  serviceName?: string | null      // joined for display
  costType: CostType
  unitCost: number
  currency: string
  effectiveFrom: string
  effectiveTo?: string | null
  notes?: string | null
  createdAt?: string
  updatedAt?: string
}

export type OverheadCategory = 'payroll' | 'infrastructure' | 'tooling' | 'facilities' | 'other'
export type CostClass = 'cogs' | 'opex'
export type AllocationMethod = 'unallocated' | 'per_active_user' | 'per_entity_equal'

export interface OverheadCost {
  id: string
  name: string
  category: OverheadCategory
  costClass: CostClass
  monthlyAmount: number
  currency: string
  allocationMethod: AllocationMethod
  effectiveFrom: string
  effectiveTo?: string | null
  isActive: boolean
  notes?: string | null
  createdAt?: string
  updatedAt?: string
}

export interface GroupDomain {
  id: string
  parentTenantId: string
  parentTenantSlug?: string | null
  emailDomain: string
  subCompanyName: string
  isIntragroup: boolean
  userCount?: number | null        // latest known headcount, if available
  autoDiscovered?: boolean         // seeded name === domain → needs owner confirmation
  notes?: string | null
}

// ============ FI overview (owner cockpit) ============

/** Package-vs-consulting revenue split for a period or window. */
export interface ServiceTypeSplit {
  packageRevenue: number
  consultingRevenue: number
  adjustmentRevenue: number
  packageCogs: number
  consultingCogs: number
}

/** Intra-group vs external split. */
export interface GroupSplit {
  intragroupRevenue: number
  externalRevenue: number
  intragroupCogs: number
  externalCogs: number
  intragroupMarginPct: number | null
  externalMarginPct: number | null
}

export interface CostBreakdownItem {
  vendor: string
  cost: number
  costType: CostType | 'overhead' | 'mixed'
  isFixed: boolean
}

/** One month in the trend series. */
export interface FiPeriod {
  year: number
  month: number
  label: string                    // e.g. "Jul 2026"
  revenue: number
  revenuePackage: number
  revenueConsulting: number
  cogs: number
  overhead: number                 // cogs-class overhead allocated/period
  grossProfit: number
  marginPct: number | null
  mrr: number                      // recurring revenue that month
}

export interface ClientMarginRow {
  entityKind: 'tenant' | 'client'
  entityId: string
  name: string
  isIntragroup: boolean
  offeringMix: 'package' | 'consulting' | 'mixed'
  revenue: number
  cogs: number
  grossProfit: number
  marginPct: number | null
  costKnown: boolean               // false if any line lacks a configured cost
  trend?: number[]                 // sparkline of recent monthly revenue
}

export interface CostCoverage {
  servicesWithConfiguredCost: number
  servicesTotal: number
  missingCostVendors: string[]
  /** true when at least one revenue bucket has managed/package lines with no cost → margin is incomplete */
  incomplete: boolean
}

export interface FiOverviewResponse {
  generatedAt: string
  /** the period the "current" P&L refers to (latest snapshot or preview month) */
  currentPeriod: { year: number; month: number; label: string; isPreview: boolean }
  trend: FiPeriod[]                // last N months, oldest→newest
  current: {
    revenue: number
    cogs: number
    overhead: number
    grossProfit: number
    marginPct: number | null
    mrr: number
    arr: number
  }
  groupSplit: GroupSplit
  serviceTypeSplit: ServiceTypeSplit
  costBreakdown: CostBreakdownItem[]
  topClientsByMargin: ClientMarginRow[]
  costCoverage: CostCoverage
  pipeline: DealsSummary
}
